import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import LoadingButton from '../fw/LoadingButton.react.js';
import DetailBox from '../fw/DetailBox.react.js';
import TableDetail from '../fw/TableDetail.react';
import {FormattedMessage, injectIntl, defineMessages, IntlShape} from 'react-intl';
import DetailToolBar from '../fw/DetailToolBar.react.js';
import DeleteButton from '../fw/DeleteButton.react';
import ResendInvitationMutation from './ResendInvitationMutation';
import {wrapNotFound} from '../app/NotFoundHandler.react';

import types from '../../common/model-api/types';
import urlMapping from '../app/URLMapping';
import EventTracker from '../app/EventTracker';
import mutationError from '../channel-defs/mutationError';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import { User } from '../../common/types/types';

const messages = defineMessages({
    title: {id: 'invitationView.title', 'defaultMessage': 'Invitation'}
});

interface InvitationViewProps {
 intl: IntlShape;
 user: User;
}

class InvitationView extends React.Component<InvitationViewProps> {

    typeName = 'invitations';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired
    };

    onClickResend = (event) => {
        return new Promise((resolve, reject) => {
            const data = this.props.user.organisation.invitation;

            let onFailure = (transaction) => {
                mutationError(transaction);
                reject();
            };

            let onSuccess = () => {
                resolve();
            };

            EventTracker.trackEvent(EventTracker.EVENT_CLICKS, {
                event: 'invitations-resend',
                type: 'application'
            });

            // To perform a mutation, pass an instance of one to `Relay.Store.commitUpdate`
            Relay.Store.commitUpdate(new ResendInvitationMutation({invitation: data}), {
                onFailure,
                onSuccess
            });
        });
    };

    authorRenderer = (author) => {
        let render = '';

        if (author.firstname) {
            render += author.firstname;
        }

        if (author.firstname && author.surname) {
            render += ' ';
        }

        if (author.surname) {
            render += author.surname;
        }

        if (render.length > 0) {
            render += ' <' + author.email + '>';
        }
        else {
            render = author.email;
        }

        return render;
    }


    render() {
        let { invitation } = this.props.user.organisation;

        let type = types[this.typeName];

        return (
            <AppPanel breadcrumbParams={[
                {type, data: invitation},
                {type: types['organisations'], data: this.props.user.organisation}]
            } title={this.props.intl.formatMessage(messages.title)}>
                <div className="event-page">

                    <DetailBox data={invitation} metadata={type} urlMapping={urlMapping}>
                        <DetailToolBar data={invitation} metadata={type} organisationId={this.props.user.organisation.realId} urlMapping={urlMapping}>
                            <DeleteButton
                                data={invitation}
                                deleteFatQuery={Relay.QL `fragment on DeleteInvitationPayload {
                                                            updatedNode {
                                                                id
                                                            },
                                                            organisation {
                                                                invitations(first: 100) {
                                                                    edges {
                                                                        node {
                                                                            id
                                                                        }
                                                                    }
                                                                }
                                                            }

                                                        }`}
                                deleteMutation={Relay.QL `mutation { deleteInvitation }`}
                                metadata={type}
                                organisationId={this.props.user.organisation.realId}
                                parent={this.props.user.organisation}
                                redirectToParent={false}
                                types={types}
                                urlMapping={urlMapping}
                            />
                            <LoadingButton className="btn btn-default" onClick={this.onClickResend}><FormattedMessage defaultMessage="Resend invitation" id="invitation.resend" /></LoadingButton>
                        </DetailToolBar>

                        <h2><FormattedMessage defaultMessage="Invitation for {email}" id="invitation.title" values={{email: invitation.email}}/></h2>

                        <TableDetail columns={['createdAt']} data={invitation} metadata={type}>
                            <tr>
                                <td><FormattedMessage defaultMessage="Invited by:" id="invitation.invitedBy" /></td>
                                <td>{this.authorRenderer(invitation.author)}</td>
                            </tr>
                        </TableDetail>
                    </DetailBox>
                </div>
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(InvitationView), (props) => {
    return props.user.organisation && props.user.organisation.invitation;
}), {
    initialVariables: {
        organisationId: '',
        invitationId: ''
    },
    prepareVariables (variables) {
        if (!variables.organisationId) {
            variables.organisationId = getLastOrganisationId();
        }

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    name,
                    invitation(id: $invitationId) {
                        id,
                        realId,
                        organisation_id,
                        email,
                        author {
                            id,
                            email,
                            firstname,
                            surname
                        },
                        permissions,
                        createdAt,
                    }
                }
            }
        `,
    },
});
